import { ColumnDef } from "@tanstack/react-table";
import { StatBox } from "../../../../components/ui/stat-box";
import { DollarSign, Truck, Users } from "lucide-react";
import { useEffect } from "react";
import { Card, CardContent } from "../../../../components/ui/card";
import {
  DataTable,
  createSortableHeader,
} from "../../../../components/ui/data-table";
import { Skeleton } from "../../../../components/ui/skeleton";
import { StatBoxPaymentRequest } from "./components/StatBoxPaymentRequest";
import { usePaymentRequest } from "./hooks/use-payment-request";
import { PaymentRequestPaidData, PaymentRequestPendingData } from "./types";
import { formatDateBR } from "../../../../utils/format-utils";

export default function GestorPaymentRequest() {
  const {
    selectedFund,
    paymentRequestData,
    isLoading,
    toast,
    handleRequestPayment,
    closeToast,
  } = usePaymentRequest();

  useEffect(() => {
    if (toast.open) {
      setTimeout(() => {
        closeToast();
      }, 6000);
    }
  }, [toast, closeToast]);

  const opsData = paymentRequestData?.opsData || [];
  const paidPayments = paymentRequestData?.pagos || [];
  const pendingPayments = paymentRequestData?.pendentes || [];

  const paidColumns: ColumnDef<PaymentRequestPaidData>[] = [
    {
      accessorKey: "ops",
      header: createSortableHeader("Operação"),
    },
    {
      accessorKey: "valor_compra",
      header: createSortableHeader("Valor Total"),
      cell: ({ row }) => {
        const value = row.getValue("valor_compra") as number;
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(value / 100);
      },
    },
    {
      accessorKey: "pagamentos_unicos",
      header: createSortableHeader("Pagamentos Únicos"),
      cell: ({ row }) => {
        const value = row.getValue("pagamentos_unicos") as number;
        return value || "0";
      },
    },
    {
      accessorKey: "data_romaneio",
      header: createSortableHeader("Data Romaneio"),
      cell: ({ row }) => {
        const value = row.getValue("data_romaneio") as string;
        return value ? formatDateBR(value) : "-";
      },
    },
  ];

  const pendingColumns: ColumnDef<PaymentRequestPendingData>[] = [
    {
      accessorKey: "ops",
      header: createSortableHeader("Operação"),
    },
    {
      accessorKey: "valor_compra",
      header: createSortableHeader("Valor Pendente"),
      cell: ({ row }) => {
        const value = row.getValue("valor_compra") as number;
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(value / 100);
      },
    },
    {
      accessorKey: "pagamentos_unicos",
      header: createSortableHeader("Pagamentos Únicos"),
      cell: ({ row }) => {
        const value = row.getValue("pagamentos_unicos") as number;
        return value || "0";
      },
    },
    {
      accessorKey: "data_romaneio",
      header: createSortableHeader("Data Romaneio"),
      cell: ({ row }) => {
        const value = row.getValue("data_romaneio") as string;
        return value ? formatDateBR(value) : "-";
      },
    },
  ];

  return (
    <div className="p-6">
      {toast.open && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <Card
            className={`${
              toast.variant === "destructive"
                ? "border-red-500 bg-red-50"
                : toast.variant === "success"
                  ? "border-green-500 bg-green-50"
                  : "border-blue-500 bg-blue-50"
            }`}
          >
            <CardContent className="p-4">
              <p className="text-sm font-medium">{toast.message}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Autorização de Pagamentos
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestão e controle de pagamentos por OPS
          </p>
        </div>

      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <Skeleton className="h-12 w-12 rounded-full" />
              </div>
              <Skeleton className="h-4 w-48 mx-auto" />
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-6 mb-8">
            <h2 className="text-xl font-semibold">Operações Disponíveis</h2>
            {opsData.length === 0 ? (
              <p className="text-muted-foreground">
                Nenhuma operação disponível
              </p>
            ) : (
              opsData.map((item, index) => {
                const ops = item.ops || `op-${index}`;
                const totalPagamentos =
                  parseInt(String(item.pagamentos_unicos)) || 0;
                const totalValorPendente =
                  parseInt(String(item.valor_compra)) || 0;
                const dateToCheckout = item.data_romaneio;

                return (
                  <div
                    key={`${ops}-${index}`}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4"
                  >
                    <div className="md:col-span-2">
                      <Card className="min-h-[140px]">
                        <CardContent className="p-6 flex flex-col justify-between h-full">
                          <div className="flex flex-col items-start justify-between">
                            <div className="flex gap-4 items-center justify-center">
                              <p className="text-sm font-medium text-muted-foreground mb-2">
                                Operação
                              </p>
                              <div>
                                <Truck className="h-6 w-6 text-primary" />
                              </div>
                            </div>
                            <p className="text-2xl font-bold text-foreground">
                              {ops}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="md:col-span-2">
                      <StatBox
                        title="Total Pagamentos"
                        value={totalPagamentos}
                        valueType="integer"
                        icon={<Users className="h-6 w-6 text-primary" />}
                      />
                    </div>

                    <div className="md:col-span-3">
                      <StatBox
                        title="Valor Pendente"
                        value={totalValorPendente}
                        valueType="cents"
                        icon={<DollarSign className="h-6 w-6 text-primary" />}
                      />
                    </div>

                    <div className="md:col-span-5">
                      <StatBoxPaymentRequest
                        title={ops}
                        value={totalValorPendente}
                        ops={ops}
                        dateToCheckout={dateToCheckout}
                        onRequestPayment={handleRequestPayment}
                        isDisabled={totalValorPendente <= 0}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="space-y-8">
            <DataTable
              columns={paidColumns}
              data={paidPayments.map((item, index) => ({
                ...item,
                id: `${item.ops}-${item.data_romaneio}` || `paid-${index}`,
              }))}
              title="Pagamentos Liquidados"
              loading={isLoading}
              exportFileName="Pagamentos_Liquidados"
            />

            <DataTable
              columns={pendingColumns}
              data={pendingPayments.map((item, index) => ({
                ...item,
                id: `${item.ops}-${item.data_romaneio}` || `pending-${index}`,
              }))}
              title="Pagamentos Pendentes"
              loading={isLoading}
              exportFileName="Pagamentos_Pendentes"
            />
          </div>
        </>
      )}
    </div>
  );
}
